/**
 * VIN validation — ISO 3779 charset + FMVSS 49 CFR §565.15 check digit.
 *
 * ⚠️ THIS IS A PORT. The authority is `src/services/sentinel/app/services/vin.py`
 * (79 tests). Every constant, rule, severity and normalisation decision below is
 * copied from it deliberately, and the two must not be allowed to drift: the
 * apply form checks a VIN here so the driver hears about a typo while they are
 * still holding the truck, and the submit endpoint checks it again there because
 * a client-side check is a courtesy, never a guarantee. If you change one, change
 * both, and re-read the Python's docstrings before you do — they carry the
 * reasoning this file only summarises.
 *
 * The reason a checksum is worth the trouble at all: the document classifier read
 * `3HAEUM MI9NL138079` off a real annual inspection. VINs exclude I, O and Q
 * (ISO 3779), so that read is wrong in at least one character and there is no way
 * to tell which. A VIN typed by the driver who owns the truck is verifiable at
 * position 9; one read by OCR is not.
 *
 * Hard vs soft is the whole API. A wrong length or an impossible character is a
 * HARD error — the driver typed something that cannot be a VIN, so make them fix
 * it. A check-digit mismatch is SOFT: warn, offer "use it anyway", and let the
 * application through. Refusing a driver's entire employment application over a
 * mod-11 checksum is the wrong trade — an explicit product call in
 * `knowledge/fleet-management/specs/2026-08-03-owner-operator-prospective-vehicle-design.md`,
 * not an oversight. Do not collapse the distinction back into a boolean.
 */

export const VIN_LENGTH = 17

/** ISO 3779 §4.2 — I, O and Q are excluded because they read as 1, 0 and 0. */
const VIN_ALLOWED = new Set('ABCDEFGHJKLMNPRSTUVWXYZ0123456789'.split(''))

/** 1-indexed, as the regulation numbers it. */
const CHECK_DIGIT_POSITION = 9

/** 49 CFR §565.15(c) transliteration. I, O and Q are absent — they cannot appear. */
const TRANSLITERATION: Record<string, number> = {
  '0': 0, '1': 1, '2': 2, '3': 3, '4': 4,
  '5': 5, '6': 6, '7': 7, '8': 8, '9': 9,
  A: 1, B: 2, C: 3, D: 4, E: 5, F: 6, G: 7, H: 8,
  J: 1, K: 2, L: 3, M: 4, N: 5, P: 7, R: 9,
  S: 2, T: 3, U: 4, V: 5, W: 6, X: 7, Y: 8, Z: 9,
}

/** §565.15(c)(2) positional weights, 1..17. Position 9 carries weight 0. */
const WEIGHTS = [8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2]

/** Whitespace and the unicode dash family — see normalizeVin. */
const STRIPPED = new Set([
  ' ', '\t', '\r', '\n', '-', '‐', '‑', '‒', '–', '—', ' ',
])

export type VinError = 'empty' | 'length' | 'charset' | 'check_digit'
export type VinSeverity = 'hard' | 'soft'

const SEVERITY: Record<VinError, VinSeverity> = {
  empty: 'hard',
  length: 'hard',
  charset: 'hard',
  // The one failure where the string is SHAPED like a VIN and the driver may
  // genuinely hold a VIN whose stamped check digit we cannot reproduce.
  check_digit: 'soft',
}

export interface VinValidation {
  /** Exactly what was typed, untouched — safe to quote back at the driver. */
  raw: string
  /** Uppercased, separators removed. Populated on failure too: a soft-blocked VIN is still stored. */
  vin: string
  isValid: boolean
  error: VinError | null
  severity: VinSeverity | null
  /** Driver-safe. Safe to render verbatim. */
  message: string
  /**
   * What §565.15 says position 9 should be.
   * **Never show this to the driver** — someone who "fixes" position 9 to match
   * produces a VIN that passes validation and identifies no vehicle.
   */
  expectedCheckDigit: string | null
}

/**
 * Uppercase, drop whitespace and hyphens — interior ones too, not just the ends.
 *
 * A VIN's charset contains neither, so removing them can never change a
 * legitimate VIN, while a driver typing off a door-jamb sticker on a phone
 * keyboard produces them constantly.
 *
 * Deliberately NOT normalised: I→1, O→0, Q→0. Those substitutions are the exact
 * bug this module exists to prevent — a silent guess at a character we cannot
 * recover. They surface as a charset error so the driver re-reads the plate.
 */
export function normalizeVin(raw: string | null | undefined): string {
  if (!raw) return ''
  return raw
    .trim()
    .toUpperCase()
    .split('')
    .filter((ch) => !STRIPPED.has(ch))
    .join('')
}

/**
 * The §565.15 check digit for a normalised 17-character VIN: "0"–"9" or "X".
 * Returns null when the input is not a well-formed VIN string.
 */
export function computeCheckDigit(vin: string): string | null {
  if (vin.length !== VIN_LENGTH) return null
  let total = 0
  for (let i = 0; i < VIN_LENGTH; i++) {
    const char = vin[i]
    const weight = WEIGHTS[i]
    if (char === undefined || weight === undefined) return null
    const value = TRANSLITERATION[char]
    if (value === undefined) return null
    total += value * weight
  }
  const remainder = total % 11
  return remainder === 10 ? 'X' : String(remainder)
}

/**
 * Validate a driver-typed VIN. Never throws — always returns a verdict.
 *
 * Checks run in order and stop at the first failure, so `error` names the most
 * fundamental thing wrong: empty → length → charset → check digit. Whether
 * "not supplied" is acceptable is the caller's business, which is why empty is
 * its own case rather than a length error.
 */
export function validateVin(raw: string | null | undefined): VinValidation {
  const submitted = raw ?? ''
  const vin = normalizeVin(raw)

  const fail = (error: VinError, message: string, expected: string | null = null): VinValidation => ({
    raw: submitted,
    vin,
    isValid: false,
    error,
    severity: SEVERITY[error],
    message,
    expectedCheckDigit: expected,
  })

  if (!vin) return fail('empty', 'Enter the 17-character VIN.')

  if (vin.length !== VIN_LENGTH) {
    return fail(
      'length',
      `A VIN is exactly ${VIN_LENGTH} characters — this one has ${vin.length}.`,
    )
  }

  const invalid = Array.from(new Set(vin.split(''))).filter((ch) => !VIN_ALLOWED.has(ch)).sort()
  if (invalid.length > 0) {
    const confusable = invalid.filter((ch) => ch === 'I' || ch === 'O' || ch === 'Q')
    const detail = confusable.length
      ? `a VIN never contains ${confusable.join(', ')} — check for 1 and 0 instead`
      : `remove ${invalid.join(', ')}`
    return fail('charset', `That VIN has a character it cannot have (${detail}).`)
  }

  const expected = computeCheckDigit(vin)
  if (vin[CHECK_DIGIT_POSITION - 1] !== expected) {
    return fail(
      'check_digit',
      "That VIN doesn't check out — please re-read it from the vehicle. " +
        "If you're sure it's right, you can submit it as typed.",
      expected,
    )
  }

  return {
    raw: submitted,
    vin,
    isValid: true,
    error: null,
    severity: null,
    message: '',
    expectedCheckDigit: expected,
  }
}

/** True when the caller may persist the VIN behind an explicit override. */
export function canStoreWithOverride(result: VinValidation): boolean {
  return result.isValid || result.severity === 'soft'
}
